import { styled, Typography, TypographyProps, useTheme } from "@mui/material";
import { forwardRef, memo } from "react";
import toast from "react-hot-toast";
import { EFontFamilies } from "@shared/theme/theme";
import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";
type f = EFontFamilies.DISPLAY | EFontFamilies.DEFAULT_FONT;
export interface MxTypographyProps extends TypographyProps {
  lines?: number;
  onlyText?: boolean;
  removehtml?: boolean;
  autoCopy?: boolean;
  removeAutoCopyStyles?: boolean;
  valueToCopy?: string;
  textTransform?: "uppercase" | "lowercase" | "capitalize";
  size?: 9 | 10 | 12 | 14 | 15 | 16 | 18 | 20 | 24;
  underline?: boolean;
  elRef?: React.RefObject<HTMLDivElement>;
}

function MxTypography(props: MxTypographyProps, ref: React.Ref<HTMLDivElement>) {
  const [text, copy] = useCopyToClipboard();
  const theme = useTheme();
  const { onlyText = true, ...rest } = props;
  if (rest.removehtml) {
    rest.dangerouslySetInnerHTML = undefined;
    rest.children = stripHtml(rest.children as any);
  }

  if (rest.autoCopy) {
    rest.onClick = () =>
      toast.promise(copy(rest.valueToCopy ?? (rest.children as string)), {
        loading: "Copying to clipboard",
        success: "Copied to clipboard",
        error: "Error copying to clipboard"
      });

    rest.lines = 1;
    rest.style = {
      cursor: "pointer",
      wordWrap: "break-word",
      ...rest.style
    };
    if (!rest.removeAutoCopyStyles) {
      rest.style = {
        color: theme.palette.primary.main,
        textDecoration: "underline",
        ...rest.style
      };
    }
  }

  return <TextCustomer {...rest} ref={ref} />;
}

export default memo(forwardRef(MxTypography));

const TextCustomer = styled(Typography, {
  shouldForwardProp: prop =>
    prop !== "removehtml" &&
    prop !== "autoCopy" &&
    prop !== "underline" &&
    prop !== "valueToCopy"
})<MxTypographyProps>(
  ({
    theme,
    lines,
    fontFamily = EFontFamilies.DEFAULT_FONT,
    textTransform,
    size,
    underline,
    fontSize
  }) => ({
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    overflow: lines ? "hidden" : "visible",
    WebkitLineClamp: lines ? lines : "unset",
    verticalAlign: "bottom",
    lineHeight: 1.1,
    height: "auto",
    textTransform,
    textDecoration: underline ? "underline" : "none"
  })
);

function stripHtml(html: any) {
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}
